'use client'

import { Heart } from 'lucide-react'
import Image from 'next/image'

import {
  CASHBACK_DEFAULT_BACKGROUND,
  CASHBACK_LUXE_BACKGROUND,
} from '@/shared/constants/cashbackConstants'
import type { BestSellingProductWithImage } from '@/features/homepage-v2/types/products.types'
import { formatCurrency } from '@/shared/utils/currency'
import DisabledCircle from '@/public/icons/DisabledCircle'
import { Button } from '@/shared/components/ui/Button'
import TruncatedTooltipText from '@/shared/components/ui/TruncatedTooltipText'
import { buildCdnUrl } from '@/shared/constants/publicUrlConstants'
import { useCardNavigation } from '@/shared/hooks/useCardNavigation'
import { calculateDiscountPercentage } from '@/shared/utils/productDiscount'
import { useWishlistStore } from '@/store'
import CashbackBadge from './CashbackBadge'
import TopRatedBadge from './TopRatedBadge'

type ProductCardProps = {
  product: BestSellingProductWithImage
  mobile?: boolean
  isDisabled?: boolean
  showWishlist?: boolean
  /** Override the card width (defaults to w-[150px] mobile / w-[140px] desktop). */
  widthClassName?: string
  /** Override the image height (defaults to h-[200px] mobile / h-[180px] desktop). */
  imageHeightClassName?: string
  /** Override the image frame corner radius (defaults to rounded-[10px]). */
  imageRadiusClassName?: string
  /** Override the next/image sizes hint to match a custom width. */
  imageSizes?: string
  /** Override the image fit/background (defaults to object-contain on a light bg). */
  imageClassName?: string
  /** Override the Top Rated badge positioning/scale. */
  badgeClassName?: string
  /** Show the mobile-only cashback badge. */
  showCashbackBadge?: boolean
  /** Selects the badge's member gradient over the default gold gradient. */
  isLuxeMember?: boolean
  /** Whether this card's cashback badge is the currently-expanded one. */
  cashbackExpanded?: boolean
  /** Called when the cashback badge is tapped, to request expansion. */
  onCashbackBadgeClick?: () => void
  /** Extra classes for the text/content wrapper (e.g. a scoped font). */
  contentClassName?: string
  /** Override the brand name text styles. */
  brandClassName?: string
  /** Override the product title text styles. */
  titleClassName?: string
  /** Override the selling price text styles. */
  priceClassName?: string
  /** Override the strikethrough compare-at price text styles. */
  comparePriceClassName?: string
  /** Override the discount (% OFF) text styles. */
  discountClassName?: string
}

const ProductCard = ({
  product,
  mobile = false,
  isDisabled = false,
  showWishlist = true,
  widthClassName,
  imageHeightClassName,
  imageRadiusClassName,
  imageSizes,
  imageClassName,
  badgeClassName,
  showCashbackBadge = false,
  isLuxeMember = false,
  cashbackExpanded = false,
  onCashbackBadgeClick,
  contentClassName,
  brandClassName,
  titleClassName,
  priceClassName,
  comparePriceClassName,
  discountClassName,
}: ProductCardProps) => {
  const { toggleItem, isInWishlist } = useWishlistStore()
  const { handleCardClick, handleCardKeyDown } = useCardNavigation(product.slug)
  const imageSrc = buildCdnUrl(product.primary_image)
  const showOriginalPrice = product.compare_at_price > product.price
  const discount = calculateDiscountPercentage({
    price: product.price,
    compareAtPrice: product.compare_at_price,
    discountPercentage: product.discount_percentage,
  })
  const inWishlist = isInWishlist(product.id)

  const handleWishlistToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isDisabled) return

    void toggleItem({
      id: product.id,
      slug: product.slug,
      title: product.title,
      price: product.price,
      compare_at_price: product.compare_at_price,
      primary_image: imageSrc,
      brand_name: product.brand_name,
      rating: product.rating,
      review_count: product.review_count,
      out_of_stock: !product.in_stock,
    })
  }

  return (
    <article
      role="link"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : handleCardClick}
      onKeyDown={isDisabled ? undefined : handleCardKeyDown}
      className={`relative shrink-0 ${widthClassName ?? (mobile ? 'w-[150px]' : 'w-[140px]')} ${
        isDisabled ? 'pointer-events-none cursor-not-allowed grayscale' : 'cursor-pointer'
      }`}
    >
      <div
        className={`relative flex items-center justify-center overflow-hidden bg-[#F9FAFB] ${
          imageRadiusClassName ?? 'rounded-[10px]'
        } ${imageHeightClassName ?? (mobile ? 'h-[200px]' : 'h-[180px]')}`}
      >
        <Image
          src={imageSrc}
          alt={product.title}
          fill
          sizes={imageSizes ?? (mobile ? '150px' : '140px')}
          quality={70}
          className={imageClassName ?? 'object-contain bg-[#F3F4F6]'}
        />
        <TopRatedBadge
          className={
            badgeClassName ??
            `absolute left-2 top-2 z-10 origin-top-left ${mobile ? 'scale-[0.875]' : 'scale-[0.79]'}`
          }
        />
        {mobile && showCashbackBadge && !isDisabled ? (
          <CashbackBadge
            amount={product.cashback}
            background={isLuxeMember ? CASHBACK_LUXE_BACKGROUND : CASHBACK_DEFAULT_BACKGROUND}
            expanded={cashbackExpanded}
            onClick={onCashbackBadgeClick}
            className="absolute right-[7.78px] top-[171.23px] z-10"
          />
        ) : null}
        {isDisabled ? (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <DisabledCircle />
          </span>
        ) : null}
      </div>

      <div className={`pt-2 ${contentClassName ?? ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p
              className={
                brandClassName ??
                `${mobile ? 'text-[12px]' : 'text-[11px]'} truncate font-semibold uppercase leading-[15px] tracking-[0.2px] text-[#111827]`
              }
            >
              {product.brand_name || 'Brand'}
            </p>
          </div>
          {showWishlist ? (
            <Button
              type="button"
              onClick={handleWishlistToggle}
              disabled={isDisabled}
              aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
              variant="ghost"
              className="-mr-0.5 mt-0.5 h-auto shrink-0 p-0 text-[#667085] transition-colors hover:bg-transparent hover:text-[#111827]"
            >
              <Heart
                className={`${mobile ? 'size-[16px]' : 'size-[15px]'} ${
                  inWishlist ? 'fill-[#E11D48] text-[#E11D48]' : ''
                }`}
                strokeWidth={1.75}
              />
            </Button>
          ) : null}
        </div>

        <div>
          <TruncatedTooltipText
            as="p"
            text={product.title}
            maxChars={mobile ? 28 : 42}
            className={
              titleClassName ??
              `mt-0.5 ${mobile ? 'text-[10px]' : 'text-[9px]'} leading-[12px] text-[#4B5563]`
            }
          />
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5">
          <span
            className={
              priceClassName ??
              `${mobile ? 'text-[12px]' : 'text-[14px]'} font-semibold leading-[15px] text-[#111827]`
            }
          >
            {formatCurrency(product.price)}
          </span>
          {showOriginalPrice ? (
            <span
              className={
                comparePriceClassName ??
                `${mobile ? 'text-[11px]' : 'text-[9px]'} leading-[12px] text-[#4B5563] line-through`
              }
            >
              {formatCurrency(product.compare_at_price)}
            </span>
          ) : null}
          {discount > 0 ? (
            <span
              className={
                discountClassName ??
                `${mobile ? 'text-[10px]' : 'text-[9px]'} leading-[12px] text-[#FF9933]`
              }
            >
              ({discount}% OFF)
            </span>
          ) : null}
        </div>

        {!product.in_stock ? (
          <p className="mt-1 text-[10px] font-medium leading-[12px] text-[#DC2626]">
            Out of stock
          </p>
        ) : null}
      </div>
    </article>
  )
}

export default ProductCard
